// API Configuration and Helper Functions
// Centralized API calls for all backend endpoints

import axios from 'axios';

// Base API URL - uses environment variable or defaults to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add auth token to requests if it exists
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Auth API calls
export const authAPI = {
    login: async (username, password) => {
        const response = await api.post('/auth/login', { username, password });
        return response.data;
    },
    
    verifyToken: async () => {
        const response = await api.get('/auth/verify');
        return response.data;
    }
};

// Flash Report API calls
export const flashReportAPI = {
    getPlants: async () => {
        const response = await api.get('/flash-report/plants');
        return response.data;
    },
    
    createReport: async (reportData) => {
        const response = await api.post('/flash-report', reportData);
        return response.data;
    },
    
    updateReport: async (reportId, reportData) => {
        const response = await api.put(`/flash-report/${reportId}`, reportData);
        return response.data;
    },
    
    getReport: async (plantId, weekEnding) => {
        const response = await api.get(`/flash-report?plant_id=${plantId}&week_ending=${weekEnding}`);
        return response.data;
    },
    
    getHistory: async (plantId, limit = 13, endingDate = null) => {
        let url = `/flash-report/history?plant_id=${plantId}&limit=${limit}`;
        if (endingDate) {
            url += `&ending_date=${endingDate}`;
        }
        const response = await api.get(url);
        return response.data;
    },
    
    getDashboard: async (weekEnding) => {
        const response = await api.get(`/flash-report/dashboard?week_ending=${weekEnding}`);
        return response.data;
    }
};

// Utilities API calls
export const utilitiesAPI = {
    createReport: async (reportData) => {
        const response = await api.post('/utilities', reportData);
        return response.data;
    },
    
    updateReport: async (reportId, reportData) => {
        const response = await api.put(`/utilities/${reportId}`, reportData);
        return response.data;
    },
    
    getReport: async (plantId, reportMonth) => {
        const response = await api.get(`/utilities?plant_id=${plantId}&report_month=${reportMonth}`);
        return response.data;
    },
    
    getHistory: async (plantId, limit = 6, endingMonth = null) => {
        let url = `/utilities/history?plant_id=${plantId}&limit=${limit}`;
        if (endingMonth) {
            url += `&ending_month=${endingMonth}`;
        }
        const response = await api.get(url);
        return response.data;
    },

        // ADD THIS NEW FUNCTION
    getDashboard: async (reportMonth) => {
        const response = await api.get(`/utilities/dashboard?report_month=${reportMonth}`);
        return response.data;
    }
};

// User Management API calls
export const userAPI = {
    getAllUsers: async () => {
        const response = await api.get('/users');
        return response.data;
    },
    
    createUser: async (userData) => {
        const response = await api.post('/users', userData);
        return response.data;
    },
    
    updateUser: async (userId, userData) => {
        const response = await api.put(`/users/${userId}`, userData);
        return response.data;
    },
    
    deleteUser: async (userId) => {
        const response = await api.delete(`/users/${userId}`);
        return response.data;
    }
};

// Settings API calls (admin only) - per-plant Utilities Per Pound switch
export const settingsAPI = {
    getUtilitiesPerLb: async () => {
        const response = await api.get('/settings/utilities-per-lb');
        return response.data;
    },

    setUtilitiesPerLb: async (plantId, enabled) => {
        const response = await api.put(`/settings/utilities-per-lb/${plantId}`, { enabled });
        return response.data;
    }
};

export default api;